import React, { useState } from "react";
import "./ForgotPasswordModal.css";
import axios from "axios";
import { getSecurityQusUrl, getForgotPwdUrl } from "../constants";

const ForgotPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const [employeeId, setEmployeeId] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // Step 1: Enter Employee ID, Step 2: Answer Security Question
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Spinner state

  const validatePassword = (pwd) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pwd);
  };

  const fetchSecurityQuestion = async (employeeId) => {
    try {
      const response = await axios.get(getSecurityQusUrl(employeeId));
      return response.data.question || null;
    } catch (error) {
      console.error("Error fetching security question:", error);
      return null;
    }
  };

  const handleEmployeeIdSubmit = async () => {
    setLoading(true);
    if (!employeeId.trim()) {
      setError("Employee ID is required.");
      setLoading(false);
      return;
    }

    setError(""); 
    const question = await fetchSecurityQuestion(employeeId);

    if (question) {
      setSecurityQuestion(question);
      setStep(2);
    } else {
      setError("Invalid Employee ID or user is deactivated.");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!answer.trim()) {
      setError("Answer to security question is required.");
      setLoading(false);
      return;
    }
    if (!validatePassword(password)) {
      setError(
        "Password must be at least 8 characters long and include an uppercase letter, a number, and a special character."
      );
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(getForgotPwdUrl(), {
        employeeId,
        securityAns: answer,
        newPassword: password,
      });

      onSuccess(response.data.message);
    } catch (error) {
      console.error("Error:", error);
      if (error.response) {
        setError(error.response.data.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fp-modal-overlay">
        {loading && (
        <div className="loading-overlay">
          <span className="spinner"></span>
        </div>
      )}
      <div className="fp-modal-box">
        <h2>Reset Password</h2>

        {step === 1 && (
          <>
            <input
              type="text"
              placeholder="Enter Employee ID"
              className="fp-input-field"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
            {error && <p className="fp-error-text">{error}</p>}
            <button className="fp-submit-button" onClick={handleEmployeeIdSubmit}>
              Submit
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="fp-security-question">
              Security Question: <strong>{securityQuestion}</strong>
            </p>
            <input
              type="text"
              placeholder="Your Answer"
              className="fp-input-field"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />

            {/* New Password Input with Eye Icon */}
            <div className="fp-password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                className="fp-input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                className="fp-eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "👁️" : "🙈"}
              </span>
            </div>

            {/* Confirm Password Input with Eye Icon */}
            <div className="fp-password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="fp-input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <span
                className="fp-eye-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "👁️" : "🙈"}
              </span>
            </div>

            {error && <p className="fp-error-text">{error}</p>}
            <button className="fp-submit-button" onClick={handleSubmit}>
              Reset Password
            </button>
          </>
        )}

        <button className="fp-close-button" onClick={onClose}>✖</button>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
